import React, { useEffect, useState } from "react";
import { useUser } from "../../context/UserContext";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import MultiPhotoCapture from "./MultiPhotoCapture";
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Button from '../UI/Button';
import actionLabels from '../../config/actionLabels';

const schema = yup.object().shape({
    username: yup.string().required("Имя обязательно"),
    password: yup.string().min(6, "Мин. 6 символов"),
    position: yup.string().required("Должность обязательна"),
    department: yup.string().required("Отдел обязателен"),
    shiftSchedule: yup.string().required("Смена обязательна"),
});

const CreateUserForm = () => {
    const [photoData, setPhotoData] = useState([]);
    const [positions, setPositions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [shifts, setShifts] = useState([]);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(schema),
    });

    const { logout, hasPermission } = useUser();
	const canCreate = hasPermission("users", "create");
    const [message, setMessage] = useState("");
    const [resetCapture, setResetCapture] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [positionsRes, departmentsRes, shiftsRes] = await Promise.all([
                    fetchWithAuth(`${process.env.REACT_APP_API_URL}/positions`, {}, logout),
                    fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/departments`, {}, logout),
                    fetchWithAuth(`${process.env.REACT_APP_API_URL}/admin/shift-schedules`, {}, logout)
                ]);
                const positionsData = await positionsRes.json();
                const departmentsData = await departmentsRes.json();
                const shiftsData = await shiftsRes.json();
                setPositions(positionsData);
                setDepartments(departmentsData);
                setShifts(shiftsData);
            } catch (err) {
                console.error("Ошибка при получении данных:", err);
            }
        };

        fetchData();
    }, []);


	if (!canCreate) {
		return <div className="text-red-500 text-center text-2xl">У вас нет прав на создание пользователей</div>;
	}

    const handleCreateUser = async (data) => {
        if (!photoData || photoData.length === 0) {
            setMessage("❌ Нужно сделать серию фото перед регистрацией");
            return;
        }

        try {
            const formData = new FormData();
            formData.append("fullName", data.fullName);
            formData.append("firstName", data.firstName);
            formData.append("lastName", data.lastName);
            formData.append("username", data.username);
            formData.append("password", data.password);
            formData.append("position", data.position);
            formData.append("department", data.department);
            formData.append("shiftSchedule", data.shiftSchedule);
            photoData.forEach((file) => {
                formData.append("photos", file);
            });

            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/users`,
                {
                    method: "POST",
                    body: formData,
                },
                logout
            );

            const responseData = await res.json();
            if (!res.ok) throw new Error(responseData.message);

            setMessage("✅ Пользователь успешно создан");
            reset();
            setPhotoData([]);
            setResetCapture(true);
            setTimeout(() => setResetCapture(false), 100); 
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setMessage(`❌ Ошибка: ${err.message}`);
        }
    };

    return (
        <div>
            {/* <h1 className="text-2xl font-bold mb-4">Создание пользователя</h1> */}
            <form
                onSubmit={handleSubmit(handleCreateUser)}
                className="space-y-4 max-w-screen-md mx-auto"
            >
                <div className="space-y-2">
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        placeholder="Фамилия пользователя"
                        {...register("lastName")}
                    />
                    {errors.lastName && (
                        <p className="text-red-500 text-sm">
                            {errors.lastName.message}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        placeholder="Имя пользователя"
                        {...register("fullName")}
                    />
                    {errors.fullName && (
                        <p className="text-red-500 text-sm">
                            {errors.fullName.message}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        placeholder="Отчество пользователя"
                        {...register("firstName")}
                    />
                    {errors.firstName && (
                        <p className="text-red-500 text-sm">
                            {errors.firstName.message}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <input
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        placeholder="Логин"
                        {...register("username")}
                    />
                    {errors.username && (
                        <p className="text-red-500 text-sm">
                            {errors.username.message}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <input
                        type="password"
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        placeholder="Пароль"
                        {...register("password")}
                    />
                    {errors.password && (
                        <p className="text-red-500 text-sm">
                            {errors.password.message}
                        </p>
                    )}
                </div>
                <div className="space-y-2">
                    <select
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        defaultValue=""
                        {...register("position")}
                    >
                        <option value="" disabled>
                            Выберите должность...
                        </option>
                        {positions.map((position) => (
                            <option key={position._id} value={position.name}>
                                {position.name}
                            </option>
                        ))}
                    </select>
                    {errors.position && (
                        <p className="text-red-500 text-sm">
                            {errors.position.message}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <select
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        defaultValue=""
                        {...register("department")}
                    >
                        <option value="" disabled>Выберите отдел...</option>
                        {departments.map((dept) => (
                            <option key={dept._id} value={dept._id}>
                                {dept.name}
                            </option>
                        ))}
                    </select>
                    {errors.department && (
                        <p className="text-red-500 text-sm">
                            {errors.department.message}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <select
                        className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42] focus:outline-none focus:border-[#007ACC]"
                        defaultValue=""
                        {...register("shiftSchedule")}
                    >
                        <option value="" disabled>Выберите смену...</option>
                        {shifts.map((shift) => (
                            <option key={shift._id} value={shift._id}>
                                {shift.name} ({shift.startTime} - {shift.endTime})
                            </option>
                        ))}
                    </select>
                    {errors.shiftSchedule && (
                        <p className="text-red-500 text-sm">
                            {errors.shiftSchedule.message}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <MultiPhotoCapture 
                        resetCapture={resetCapture}
                        onCaptureComplete={setPhotoData} 
                    />
                </div>

                <div className="flex justify-end">
                    <Button
                        type="submit"
                        variant="primary"
                    >
                        Создать
                    </Button>
                </div>
            </form>
            {message && (
                <div className="mt-4 p-2 text-sm rounded bg-[#1E1E1E] border border-[#3E3E42]">
                    {message}
                </div>
            )}
        </div>
    );
};

export default CreateUserForm;
